<script lang="ts">
	import { real_id } from 'src/helpers/interface_helpers';
	import { format } from 'svelte-i18n';
	import { Alert, Button, Container, Text } from '@Shelly/shelly-design-system';
	import DeleteDeviceModal from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/DeleteDeviceModal.svelte';
	import {
		getAlarmsWithDevice,
		getDashboardsWithDevice,
		getGroupsWithDevice,
		getScenesWithDevice,
		getThermostatsWithDevice,
		type DeviceUsage,
	} from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/device_usage_helper';
	import {
		getIncompleteGatewayZWaveNodes,
		getOrphanZWaveCloudDevices,
		parseZWaveNodeIdFromDeviceId,
	} from 'src/helpers/z_wave_gateway_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { devices, devices_status } from 'src/stores';
	import { rooms } from 'src/stores';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	$: status = $devices_status?.[real_id(device?.id)];

	$: missingDevices = getOrphanZWaveCloudDevices(device, status, $devices);
	$: incompleteNodes = getIncompleteGatewayZWaveNodes(status);

	let showModalDelete = false;
	let deleteTarget: Device | undefined;
	let deleteDeviceUsage: DeviceUsage = {
		room: [],
		group: [],
		alarm: [],
		scene: [],
		dashboard: [],
		thermostat: [],
	};
	let deleteDeviceIsUsed = false;

	const openDeleteModal = (missingDevice: Device) => {
		deleteTarget = missingDevice;
		deleteDeviceUsage = {
			room: $rooms[missingDevice.room_id] !== undefined ? [$rooms[missingDevice.room_id]] : [],
			group: getGroupsWithDevice(missingDevice.id),
			alarm: getAlarmsWithDevice(missingDevice.id),
			scene: getScenesWithDevice(missingDevice.id),
			dashboard: getDashboardsWithDevice(missingDevice.id),
			thermostat: getThermostatsWithDevice(missingDevice.id),
		};
		deleteDeviceIsUsed = Object.values(deleteDeviceUsage).some((items) => items.length > 0);
		showModalDelete = true;
	};
</script>

{#if status?.online}
	{#if missingDevices.length > 0}
		<Alert
			class="mb-s"
			type="warning"
			isHeadingBold
			heading={capFirstChar(
				$format('alert_heading_zwave_missing_devices', {
					default: 'Devices missing from gateway',
				}),
			)}
			message={$format('alert_message_zwave_missing_devices', {
				default:
					'These devices are still in your account but are not on this gateway. You can remove them from the app.',
			})}
		/>
		{#each missingDevices as missingDevice (missingDevice.id)}
			<Container type="borderSolid" backgroundColor="color-base" class="mb-s">
				<div class="d-flex align-items-center justify-content-between gap-s">
					<div class="min-width-0">
						<Text styleType="body" class="text-bold text-truncate">{missingDevice.name}</Text
						>
						<Text styleType="tiny" class="text-muted">
							{missingDevice.id}
							{#if parseZWaveNodeIdFromDeviceId(missingDevice.id) !== undefined}
								· Node {parseZWaveNodeIdFromDeviceId(missingDevice.id)}
							{/if}
						</Text>
					</div>
					<Button variant="danger" size="s" on:click={() => openDeleteModal(missingDevice)}>
						{capFirstChar($format('button_delete', { default: 'Delete' }))}
					</Button>
				</div>
			</Container>
		{/each}
	{:else}
		<Alert
			class="mb-s"
			type="info"
			isHeadingBold
			heading={capFirstChar(
				$format('alert_heading_zwave_no_missing_devices', {
					default: 'No missing devices',
				}),
			)}
			message={$format('alert_message_zwave_no_missing_devices', {
				default: 'All Z-Wave devices in this room are present on the gateway.',
			})}
		/>
	{/if}

	{#if incompleteNodes.length > 0}
		<Alert
			class="mb-s"
			type="warning"
			isHeadingBold
			heading={capFirstChar(
				$format('alert_heading_zwave_incomplete_nodes', {
					default: 'Nodes without components',
				}),
			)}
			message={$format('alert_message_zwave_incomplete_nodes', {
				default: 'These nodes are reported by the gateway but have no matching components yet.',
			})}
		/>
		{#each incompleteNodes as node (node.component_id)}
			<Container type="borderSolid" backgroundColor="color-base" class="mb-s">
				<Text styleType="body" class="text-bold">
					Node {node.node_id}
				</Text>
				<Text styleType="tiny" class="text-muted">
					zwavedevice:{node.component_id}
					{#if node.fw_ver}
						· FW {node.fw_ver}
					{/if}
				</Text>
			</Container>
		{/each}
	{/if}
{:else}
	<Alert
		type="warning"
		isHeadingBold
		heading={capFirstChar(
			$format('alert_heading_device_offline', {
				default: 'Device is offline',
			}),
		)}
		message={$format('alert_message_zwave_gateway_offline_missing_devices', {
			default: 'Bring the gateway online to check for devices missing from the gateway.',
		})}
	/>
{/if}

{#if showModalDelete && deleteTarget}
	<DeleteDeviceModal
		bind:showModalDelete
		device={deleteTarget}
		deviceIsUsed={deleteDeviceIsUsed}
		deviceUsage={deleteDeviceUsage}
		on:close={() => {
			showModalDelete = false;
			deleteTarget = undefined;
		}}
	/>
{/if}
